import { AlertTriangle, Inbox, SearchX, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * An empty screen has to answer three questions: what is missing, why it might
 * be missing, and what to do about it. A blank panel answers none of them.
 *
 * The three kinds are genuinely different situations and must not read the
 * same: nothing has been created yet, a search excluded everything, or the
 * request failed. Only the first is a good thing.
 */
const KIND = {
  empty: { icon: Inbox, tint: "bg-surface-inset text-fg-subtle" },
  search: { icon: SearchX, tint: "bg-surface-inset text-fg-subtle" },
  error: { icon: AlertTriangle, tint: "bg-danger-soft text-danger" },
} as const;

export function EmptyState({
  kind = "empty",
  icon,
  title,
  description,
  action,
  className,
}: {
  kind?: keyof typeof KIND;
  /** Overrides the icon the kind would pick. */
  icon?: LucideIcon;
  title: string;
  description: string;
  action?: ReactNode;
  className?: string;
}) {
  const Icon = icon ?? KIND[kind].icon;
  return (
    <div className={cn("flex flex-col items-center gap-3 px-6 py-16 text-center", className)}>
      <span className={cn("grid size-11 place-items-center rounded-full", KIND[kind].tint)}>
        <Icon className="size-5" aria-hidden />
      </span>
      <p className="text-title-3 text-heading">{title}</p>
      <p className="text-fg-muted text-callout max-w-sm text-balance">{description}</p>
      {action ? <div className="mt-2 flex items-center gap-2">{action}</div> : null}
    </div>
  );
}
